import { useMemo } from 'react';

import opalOrb from '@frontend/assets/opal.png';
import { useAskOpal } from '@frontend/features/chat/hooks/useAskOpal';
import { useIsOnOverlayPage } from '@frontend/features/chat/hooks/useIsOnOverlayPage';

import { type TActionInput, type TModePath } from '../store';

import * as styles from './commands.css';

// 16px inline size is required: Axiom author CSS overrides width/height attrs, so
// the orb must be sized via inline style (see OpalOrbIcon).
const orb = (
  <img
    alt=""
    aria-hidden="true"
    src={opalOrb}
    style={{ display: 'block', height: 16, width: 16 }}
  />
);

/**
 * The "Ask Opal" fallback command: shown as the sole result when a non-blank query
 * matches nothing else. Registered only on pages where the chat overlay can open,
 * so it never dead-ends. Executing it opens the embedded overlay and auto-sends the
 * typed query as a real user turn via the federated prompt queue.
 */
export function useAskOpalAction(): Record<TModePath, TActionInput[]> {
  const askOpal = useAskOpal();
  const isOnOverlayPage = useIsOnOverlayPage();

  return useMemo<Record<TModePath, TActionInput[]>>(() => {
    const actions: Record<TModePath, TActionInput[]> = {};
    if (!isOnOverlayPage) return actions;

    const action: TActionInput = {
      key: 'ask-opal',
      // `type`/`group` are intentionally unregistered: a `fallback` action supplying its
      // own `actionItemRender` is drawn by the selector's fallback branch, which consults
      // neither the type registry nor a registered group.
      type: 'ask-opal',
      group: 'ask-opal',
      fallback: true,
      label: ctx => `Ask Opal "${ctx.query.trim()}"`,
      inputAdornment: orb,
      actionItemRender: ({ ctx }) => (
        <span className={styles.row}>
          {orb}
          <span style={{ whiteSpace: 'nowrap' }}>Ask Opal</span>
          <span className={styles.askOpalQuery}>&nbsp;&ldquo;{ctx.query.trim()}&rdquo;</span>
        </span>
      ),
      // Opens the overlay in Opal-chat mode and sends the query in a fresh thread.
      execute: ctx => askOpal(ctx.query),
    };
    actions['/'] = [action];
    return actions;
  }, [isOnOverlayPage, askOpal]);
}
